import { useState, useEffect } from 'react';
import Styles from './TopicsSelect.module.scss';
import { useAPI, useNavigate, ViewContext } from 'components/lib';
import { AivagamButton } from 'components/global/AivagamButton/AivagamButton';
import Dialog from '@mui/material/Dialog';
import {
    Autocomplete,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Typography,
} from '@mui/material';
import axios from 'axios';
import { useContext } from 'react';
import { LoadingBackdrop } from 'components/global/LoadingBackdrop/LoadingBackdrop';
import { PaymentDialog } from '../PaymentDialog/PaymentDialog';
import { CurrentPlanStatus } from '../CurrentPlanStatus/CurrentPlanStatus';

export function TopicsSelect() {
    const navigate = useNavigate();
    const viewContext = useContext(ViewContext);
    const { data: topicsData, loading } = useAPI('/api/interview/topics');
    const [topics, setTopics] = useState([]);
    const [subTopics, setSubTopics] = useState([]);
    const [selectedTopic, setSelectedTopic] = useState(null);
    const [selectedSubTopic, setSelectedSubTopic] = useState(null);
    const [showTrialDialog, setShowTrialDialog] = useState(false);
    const [remainingAttempts, setRemainingAttempts] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [showUpgradePlan, setShowUpgradePlan] = useState(false);
    const [plans, setPlans] = useState([]);
    const [totalSessions, setTotalSessions] = useState(0);
    const [isTrial, setIsTrial] = useState(null);


    useEffect(() => {
        const checkPractice = async () => {
            setIsLoading(true)
            try {
                const res = await axios.get("/api/interview/user/check");
                if (!res.data.hasPracticeRecord) {
                    setShowTrialDialog(true);
                } else {
                    setRemainingAttempts(res?.data?.remainingSessions);
                    setTotalSessions(res?.data?.totalAttempts);
                    setIsTrial(res?.data?.isTrial);
                }
            }
            catch (err) {
                navigate("/oops")
            } finally {
                setIsLoading(false)
            }
        };
        checkPractice();
    }, [])

    useEffect(() => {
        if (topicsData) {
            setTopics(topicsData);
        }
    }, [topicsData]);

    useEffect(() => {
        if (selectedTopic) {
            sessionStorage.setItem('topic', JSON.stringify(selectedTopic));
        } else {
            sessionStorage.removeItem('topic');
        }
    }, [selectedTopic]);

    useEffect(() => {
        if (selectedSubTopic) {
            sessionStorage.setItem('subtopic', JSON.stringify(selectedSubTopic));
        } else {
            sessionStorage.removeItem('subtopic');
        }
    }, [selectedSubTopic]);

    useEffect(() => {
        const upgradePlan = async () => {
            try {
                const res = await axios.get("/api/interview/upgrade/plans");
                setPlans(res?.data.data || []);

            } catch (error) {
                viewContext.notification.show(
                    'Error to loading plans. Please try again later',
                    'error',
                    true,
                );
            }
        }
        upgradePlan();
    }, [])

    const handleTopicChange = (event, newValue) => {
        if (newValue === null) {
            setSelectedTopic(null);
            return;
        }
        setSelectedTopic(newValue);
        setSelectedSubTopic(null)

        const options = {
            method: 'GET',
            url: `/api/interview/topics/${newValue.id}/subtopics`,
            headers: {
                accept: 'application/json',
            },
        };

        axios
            .request(options)
            .then(function (response) {
                setSubTopics(response.data.subtopics);
            })
            .catch((err) => {
                setSelectedTopic(null);
                if (err.code === 'ECONNRESET') {
                    console.log('Connection was reset by the server.');
                    // Optionally retry or log for further inspection
                } else {
                    console.log('An unexpected error occurred:', err);
                }
                viewContext.notification.show(`Error fetching subtopics for ${newValue.topic_name}`, 'error', true);
            });
    };

    const handleSubTopicChange = (event, newValue) => {
        if (newValue === null) {
            setSelectedSubTopic(null);
            return;
        }
        setSelectedSubTopic(newValue);
    };

    const handleNext = () => {
        navigate('/interview/profile');
    };


    const handleStartTrial = async () => {
        setIsLoading(true);
        try {
            const response = await axios.post('/api/interview/user/trial/start');
            const { remainingSessions, totalAttempts, isTrial } = response?.data
            setRemainingAttempts(remainingSessions);
            setTotalSessions(totalAttempts);
            setShowTrialDialog(false);
            setIsTrial(isTrial);
        } catch (error) {
            viewContext.notification.show("Error starting free trial for interview practice. Please try again later", "error", true)
        } finally {
            setIsLoading(false);
        }
    }

    const onUpgradePlan = async (plan) => {
        try {
            setIsLoading(true);
            const options = {
                method: 'POST',
                url: '/api/interview/user/upgrade',
                headers: {
                    accept: 'application/json'
                },
                data: { packId: plan?.product_id }
            }
            const response = await axios.request(options);
            const { checkout_url } = response?.data;
            if (checkout_url) {
                window.location.href = checkout_url;
            }
        } catch (error) {
            viewContext.notification.show(
                'Error to updating plan. Please restart the interview practice',
                'error',
                true,
            );
        } finally {
            setIsLoading(false);
        }

    }

    const handleCloseUpgradePlan = () => {
        setShowUpgradePlan(false);
    }

    return (
        <>
            {(loading || isLoading) ? (
                <LoadingBackdrop isOpen={isLoading} />
            ) : (
                <div className={Styles.ancestor_container}>
                    {/* welcome dialog*/}
                    <Dialog
                        id='welcomeDialog'
                        open={showTrialDialog}
                        PaperProps={{
                            sx: {
                                width: "50vw",
                                maxWidth: "50vw"
                            }
                        }}
                        slotProps={{
                            backdrop: {
                                sx: {
                                    backgroundColor: "rgba(0,0,0,0.35)",
                                    backdropFilter: "blur(6px)",
                                },
                            },
                        }}
                    >
                        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="h6" component="span" sx={{ fontWeight: 600 }}>
                                Welcome to Interview Practice
                            </Typography>
                        </DialogTitle>
                        <DialogContent>
                            <p className={Styles.planCardText}>
                                You have <span style={{ fontWeight: 600 }}>5 free</span> attempts to try out our platform and sharpen your skills.
                                Make the most of them and get ready to ace your next interview!
                            </p>
                        </DialogContent>
                        <DialogActions sx={{ p: 2 }}>
                            <>
                                <AivagamButton
                                    id="cancelTrialBtn"
                                    onClick={() => {
                                        setShowTrialDialog(false);
                                        navigate('/navigate');
                                    }}
                                    type="cancel"
                                    buttonText={'Go Back'}
                                    classStyles={{ width: '10em' }}
                                />
                                <AivagamButton
                                    id="confirmTrialBtn"
                                    onClick={handleStartTrial}
                                    buttonText={"Start Trial"}
                                    type="confirm"
                                    classStyles={{ width: '10em' }}
                                />
                            </>
                        </DialogActions>
                    </Dialog>

                    {/* Upgrade plan dialog */}
                    <PaymentDialog
                        showUpgradePlan={showUpgradePlan}
                        handleCloseUpgradePlan={handleCloseUpgradePlan}
                        onUpgradePlan={onUpgradePlan}
                        plans={plans}
                    />
                    <div className={Styles.backBtnContainer}>
                        <button
                            type="button"
                            className={Styles.back_btn}
                            onClick={() => navigate('/navigate')}
                        >
                            {'< Back'}
                        </button>
                    </div>
                    {/* Plan status card */}
                    {(totalSessions > 0) && <CurrentPlanStatus
                        remainingAttempts={remainingAttempts}
                        setShowUpgradePlan={setShowUpgradePlan}
                        totalSessions={totalSessions}
                        isTrialPack={isTrial}
                    />}

                    <div className={Styles.cardContainer}>
                        <h6 className={Styles.heading}>Interview Practice</h6>
                        <div className={Styles.cardContent}>
                            <h4>What topic do you want to practice for?</h4>
                            <Autocomplete
                                value={selectedTopic}
                                onChange={handleTopicChange}
                                options={topics}
                                getOptionLabel={(option) => option.topic_name}
                                renderInput={(params) => (
                                    <TextField {...params} label="Select a Topic" variant="outlined" fullWidth />
                                )}
                                loading={loading}
                                disabled={remainingAttempts <= 0}
                                loadingText="Loading Topics..."
                            />
                            <h4>What subtopic do you want to select?</h4>
                            <Autocomplete
                                value={selectedSubTopic}
                                onChange={handleSubTopicChange}
                                options={subTopics}
                                getOptionLabel={(option) => option.subtopic}
                                disabled={!selectedTopic || (remainingAttempts <= 0)}
                                renderInput={(params) => (
                                    <TextField {...params} label="Select a Sub Topic" variant="outlined" fullWidth />
                                )}
                            />
                        </div>

                        <AivagamButton
                            id="nextButton"
                            buttonText="Next"
                            onClick={handleNext}
                            disabled={!selectedTopic || !selectedSubTopic || (remainingAttempts <= 0)}
                            classStyles={{
                                width: '8em',
                                boxShadow: '2px 4px 4px -1px rgba(0, 0, 0, 0.1)',
                                marginTop: '2em', // Added bottom margin
                            }}
                        />
                    </div>
                </div >
            )}
        </>
    );
}